// HM-363..366 cluster 16.2: Check-out modal pixel-aware vs
// docs/design-references/checkout-with-override-mockup.html.
//
// Sub-componenti privati nel file: GuestCard, TimelineSection,
// CostsSection, TotalsCard, PaymentMethodCard, RemoveExtraHourConfirm.
// Pattern coerente con WalkInModal (cluster 16.1.5).

import {
  AlertTriangle,
  Banknote,
  Clock,
  CreditCard,
  LogOut,
  Phone,
  X,
} from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  type CheckoutSummary,
  type ExtraHourLogEntry,
  fetchCheckoutSummary,
  finalizeCheckout,
  removeExtraHour,
} from '../../api/checkout.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../stores/auth.js';
import { useCheckoutModal } from '../../stores/checkout.js';
import { UnpaidBadge } from '../UnpaidBadge.js';

export function CheckoutModal() {
  const { isOpen, room, booking, close } = useCheckoutModal();
  const queryClient = useQueryClient();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ExtraHourLogEntry | null>(null);
  // ONDATA 1 fix 1e: contanti ricevuti dall'ospite (opzionale). Inviato
  // come amountReceived al POST /checkout; il backend valida >= saldo e
  // ritorna il resto.
  const [amountReceived, setAmountReceived] = useState('');
  // QA sweep 2026-07-22 (bug ALTO): metodo di incasso SELEZIONABILE.
  // null = l'operatore non ha ancora scelto → vale il default derivato
  // (metodo del pagamento d'ingresso, altrimenti contanti).
  const [selectedMethod, setSelectedMethod] = useState<'cash' | 'card' | null>(null);

  useEffect(() => {
    if (isOpen) {
      setErrMsg(null);
      setAmountReceived('');
      setSelectedMethod(null);
    }
  }, [isOpen]);

  const summaryQuery = useQuery({
    queryKey: ['checkout-summary', booking?.id],
    queryFn: () => fetchCheckoutSummary(booking?.id as string),
    enabled: !!booking?.id && isOpen,
    staleTime: 5_000,
  });

  const finalizeMutation = useMutation({
    mutationFn: (paymentMethod: 'cash' | 'card') => {
      const received = Number.parseFloat(amountReceived);
      return finalizeCheckout(booking?.id as string, {
        paymentMethod,
        ...(paymentMethod === 'cash' && amountReceived !== '' && Number.isFinite(received)
          ? { amountReceived: received }
          : {}),
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['rooms'] }),
        queryClient.invalidateQueries({ queryKey: ['bookings'] }),
      ]);
      close();
    },
    onError: (err: unknown) => setErrMsg(formatError(err)),
  });

  if (!isOpen || !room || !booking) return null;

  const summary = summaryQuery.data;
  const isLoading = summaryQuery.isLoading;
  // QA sweep 2026-07-22 (bug ALTO): prima il metodo era FISSO al metodo
  // d'ingresso (setter finto) e per i "paga all'uscita" il default era
  // card → l'operatore incassava contanti ma la cassa registrava Carte.
  // Default: metodo del pagamento d'ingresso se esiste, altrimenti
  // contanti (in un hotel a ore il contante e' la norma all'uscita).
  const defaultPaymentMethod: 'cash' | 'card' = summary?.paymentInitial
    ? summary.paymentInitial.method === 'cash'
      ? 'cash'
      : 'card'
    : 'cash';
  const paymentMethod = selectedMethod ?? defaultPaymentMethod;

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: overlay manuale; <dialog> popover non adatto.
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-title"
      data-testid="checkout-modal"
      className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto p-5"
      style={{ background: 'rgba(45, 36, 29, 0.5)' }}
    >
      <div
        className="bg-bg-card rounded-2xl overflow-hidden mt-7 mb-7 max-h-[90vh] flex flex-col"
        style={{
          width: 'min(760px, 96vw)',
          boxShadow: '0 24px 64px rgba(45, 36, 29, 0.25)',
        }}
      >
        <ModalHeader roomCode={room.code} onClose={close} />

        <div className="px-7 py-6 flex flex-col gap-5 flex-1 overflow-y-auto">
          {isLoading && (
            <p className="text-text-secondary text-sm" data-testid="checkout-loading">
              Caricamento riepilogo…
            </p>
          )}
          {summaryQuery.isError && !isLoading && (
            <div
              role="alert"
              data-testid="checkout-summary-error"
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col gap-2"
            >
              <p className="text-sm font-semibold text-red-700">
                Impossibile caricare i dettagli del checkout.
              </p>
              <p className="text-xs text-red-600">{formatError(summaryQuery.error)}</p>
              <button
                type="button"
                onClick={() => summaryQuery.refetch()}
                className="self-start text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-700 hover:bg-red-100"
              >
                Riprova
              </button>
            </div>
          )}
          {summary && (
            <>
              {/* Revisione pagamenti 2026-07-22: banner "DA INCASSARE" in alto
                  quando il soggiorno ha un residuo da saldare (paga all'uscita
                  o extra maturati). balanceDue = totalDue − paidAmount reale. */}
              {booking.status === 'checked_in' && Number(summary.totals.balanceDue) > 0 && (
                <div
                  data-testid="checkout-unpaid-banner"
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border"
                  style={{ background: '#FBEEEC', borderColor: '#E7C1BB' }}
                >
                  <span className="text-sm font-semibold text-[#7D2E26]">
                    Soggiorno con saldo da incassare
                  </span>
                  <UnpaidBadge amount={Number(summary.totals.balanceDue)} />
                </div>
              )}
              <GuestCard
                guest={summary.guest}
                paymentInitial={summary.paymentInitial}
                paidAmount={summary.booking.paidAmount}
              />
              <TimelineSection summary={summary} />
              <CostsSection summary={summary} onRemoveClick={(e) => setRemoveTarget(e)} />
              <TotalsCard totals={summary.totals} />
              {/* ONDATA 1 fix 1c: riga arancione — l'ospite ha pagato piu'
                  del dovuto; il surplus NON viene toccato dal checkout e va
                  rimborsato a mano. */}
              {Number(summary.totals.guestCredit ?? '0') > 0 && (
                <div
                  data-testid="guest-credit-row"
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border"
                  style={{ background: '#FFF4E5', borderColor: '#F5C77D' }}
                >
                  <span className="text-sm font-semibold" style={{ color: '#B45309' }}>
                    Credito ospite da rimborsare
                  </span>
                  <span className="text-sm font-bold tabular-nums" style={{ color: '#B45309' }}>
                    EUR {summary.totals.guestCredit}
                  </span>
                </div>
              )}
              <PaymentMethodCard
                method={paymentMethod}
                onMethodChange={setSelectedMethod}
                amountReceived={amountReceived}
                onAmountReceivedChange={setAmountReceived}
                balanceDue={summary.totals.balanceDue}
              />
            </>
          )}
          {errMsg && (
            <p role="alert" data-testid="checkout-error" className="text-sm text-red-600">
              {errMsg}
            </p>
          )}
        </div>

        <ModalFooter
          onCancel={close}
          onConfirm={() => finalizeMutation.mutate(paymentMethod)}
          loading={finalizeMutation.isPending}
          balanceDue={summary?.totals.balanceDue ?? '0.00'}
        />
      </div>

      {removeTarget && (
        <RemoveExtraHourConfirm
          entry={removeTarget}
          onCancel={() => setRemoveTarget(null)}
          onConfirmed={() => {
            setRemoveTarget(null);
            queryClient.invalidateQueries({ queryKey: ['checkout-summary', booking.id] });
          }}
        />
      )}
    </div>
  );
}

// --------------------------- ModalHeader ---------------------------

function ModalHeader({ roomCode, onClose }: { roomCode: string; onClose: () => void }) {
  return (
    <header className="shrink-0 flex items-center justify-between px-7 py-5 border-b border-border-base">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-[10px] bg-accent-soft flex items-center justify-center">
          <LogOut size={22} className="text-accent-primary" />
        </span>
        <div>
          <h2
            id="checkout-title"
            className="text-lg font-bold text-text-primary tracking-[-0.01em]"
          >
            Check-out
          </h2>
          <p className="text-xs text-text-secondary mt-0.5">Camera {roomCode}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Chiudi"
        data-testid="checkout-close"
        className="h-11 w-11 min-h-[44px] min-w-[44px] rounded-lg bg-bg-card border border-border-base flex items-center justify-center text-text-secondary hover:bg-bg-base"
      >
        <X size={18} />
      </button>
    </header>
  );
}

// --------------------------- GuestCard ---------------------------

function GuestCard({
  guest,
  paymentInitial,
  paidAmount,
}: {
  guest: CheckoutSummary['guest'];
  paymentInitial: CheckoutSummary['paymentInitial'];
  /**
   * Fix label estensioni: la riga "Pagato all'ingresso" usa il pagato
   * REALE (booking.paidAmount), non paymentInitial.amount che puo'
   * divergere (es. payment pending / totale aggiornato dall'estensione).
   */
  paidAmount: string;
}) {
  const initials = guest
    ? `${guest.firstName.charAt(0)}${guest.lastName.charAt(0)}`.toUpperCase()
    : '#';
  const fullName = guest ? `${guest.firstName} ${guest.lastName}`.trim() : 'Ospite anonimo';
  return (
    <div
      className="flex items-center gap-4 p-4 rounded-xl"
      style={{ background: '#FCF1EA' }}
      data-testid="guest-card"
    >
      <span className="w-12 h-12 rounded-full bg-accent-primary text-white flex items-center justify-center font-bold text-base">
        {initials}
      </span>
      <div className="flex-1 flex flex-col gap-1">
        <p className="text-base font-semibold text-text-primary">{fullName}</p>
        <div className="flex items-center gap-4 text-xs text-text-secondary flex-wrap">
          {guest?.phone && (
            <span className="flex items-center gap-1.5">
              <Phone size={13} />
              {guest.phone}
            </span>
          )}
          {paymentInitial && (
            <span className="flex items-center gap-1.5">
              <CreditCard size={13} />
              Pagato all'ingresso: EUR {paidAmount} (
              {paymentInitial.method === 'cash' ? 'contanti' : 'carta'})
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// --------------------------- TimelineSection ---------------------------

function TimelineSection({ summary }: { summary: CheckoutSummary }) {
  const start = new Date(summary.booking.startAt);
  const end = new Date(summary.booking.endAt);
  const fmtTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const fmtDay = (d: Date) => d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });

  // HM-540 fix UI: timeline overstay multi-giorno raggruppata.
  const extraGroups = groupExtrasByDay(summary.extraHoursLog);
  const events: { time: string; event: string; desc?: string }[] = [
    {
      time: `${fmtDay(start)} ${fmtTime(start)}`,
      event: 'Check-in',
      desc: `Pagato base EUR ${summary.totals.baseTotal}`,
    },
    {
      time: `${fmtDay(end)} ${fmtTime(end)}`,
      event: 'Uscita prevista',
    },
    ...extraGroups
      .filter((g) => g.activeCount > 0 || g.entries.some((e) => e.removed))
      .map((g) => ({
        time: g.dateLabel,
        event: `${g.activeCount} ore extra`,
        desc: g.activeCount > 0 ? `+EUR ${g.activeSum.toFixed(2)}` : undefined,
      })),
    {
      time: `${fmtDay(new Date())} ${fmtTime(new Date())}`,
      event: 'Check-out',
    },
  ];

  return (
    <section data-testid="timeline-section">
      <h3 className="text-xs uppercase tracking-[0.5px] font-semibold text-text-secondary mb-2 flex items-center gap-2">
        <Clock size={14} /> Timeline soggiorno
      </h3>
      <div
        className="rounded-xl border border-border-base p-4 flex flex-col gap-2"
        style={{ background: '#FAF7F2' }}
      >
        {events.map((e, i) => (
          <div
            key={`${e.time}-${e.event}-${i}`}
            className="grid grid-cols-[60px_1fr_auto] gap-3 text-sm"
          >
            <span className="font-mono text-text-muted">{e.time}</span>
            <span className="text-text-primary font-semibold">{e.event}</span>
            {e.desc && <span className="text-text-secondary text-right">{e.desc}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

// --------------------------- CostsSection ---------------------------

// HM-540 fix UI: per soggiorni in overstay multi-giorno, 48 righe "Ora
// extra N" sono illeggibili. Raggruppo per giorno solare (Europe/Rome)
// del campo `added_at`. Se un gruppo ha >= 6 ore, lo mostro AGGREGATO
// come "Notte/giorno extra <date>". Soglia: 6 ore = "blocco notte" dato
// che il cap è 12 ore × €5 = €60. I gruppi < 6 ore restano singoli
// (cosi' continui a vedere "Ora 3a +€10" del supplement hour-3).
type ExtraGroup = {
  key: string;
  dateLabel: string;
  entries: ExtraHourLogEntry[];
  activeCount: number;
  activeSum: number;
};

function groupExtrasByDay(rows: ExtraHourLogEntry[]): ExtraGroup[] {
  const map = new Map<string, ExtraGroup>();
  for (const e of rows) {
    const d = new Date(e.addedAt);
    // Date locale Roma (per coerenza ospite italiano).
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        dateLabel: d.toLocaleDateString('it-IT', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
        }),
        entries: [],
        activeCount: 0,
        activeSum: 0,
      };
      map.set(key, g);
    }
    g.entries.push(e);
    if (!e.removed) {
      g.activeCount++;
      g.activeSum += Number.parseFloat(e.amount);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
}

function CostsSection({
  summary,
  onRemoveClick,
}: {
  summary: CheckoutSummary;
  onRemoveClick: (entry: ExtraHourLogEntry) => void;
}) {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const canRemove =
    summary.overrideRole === 'admin_only'
      ? role === 'admin'
      : role === 'admin' || role === 'reception';

  const groups = groupExtrasByDay(summary.extraHoursLog);
  // Aggrega: un gruppo con ≥ 6 ore attive è "notte/giorno extra".
  const AGGREGATE_THRESHOLD = 6;
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  return (
    <section data-testid="costs-section">
      <h3 className="text-xs uppercase tracking-[0.5px] font-semibold text-text-secondary mb-2">
        Costi soggiorno
      </h3>
      <div className="flex flex-col gap-2">
        <CostRow
          icon={<CreditCard size={16} />}
          iconClass="bg-[#F4FAF1] border-[#C5DBB8] text-[#5F8A50]"
          label="Tariffa base"
          // Fix label estensioni: baseTotal e' il totale CORRENTE del
          // soggiorno (aggiornato da eventuali estensioni), non quanto
          // pagato al check-in — la vecchia desc "Soggiorno originario
          // (pagato al check-in)" prometteva il pagato mostrando il totale.
          desc="Soggiorno (aggiornato)"
          amount={summary.totals.baseTotal}
        />
        {groups.map((g) => {
          const aggregated = g.activeCount >= AGGREGATE_THRESHOLD;
          if (aggregated && expandedKey !== g.key) {
            return (
              <CostRow
                key={g.key}
                icon={<Clock size={16} />}
                iconClass="bg-[#FFF8EC] border-[#F5C77D] text-accent-primary"
                label={`Notte/giorno extra · ${g.dateLabel}`}
                desc={`${g.activeCount} ore extra accumulate`}
                amount={g.activeSum.toFixed(2)}
                badge={
                  <span className="text-[10px] uppercase tracking-[0.5px] font-bold px-2 py-0.5 rounded bg-accent-soft text-accent-primary-text">
                    Auto · {g.activeCount}h
                  </span>
                }
                action={
                  <button
                    type="button"
                    onClick={() => setExpandedKey(g.key)}
                    data-testid={`expand-group-${g.key}`}
                    className="text-xs font-semibold px-3 py-1 rounded-lg border border-border-input bg-bg-card text-text-secondary hover:bg-bg-muted"
                  >
                    Dettaglia
                  </button>
                }
              />
            );
          }
          // Gruppo "small" o "expanded": righe singole + bottone "comprimi".
          return (
            <div key={g.key} className="flex flex-col gap-2">
              {aggregated && (
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.5px] font-semibold text-text-muted pl-2 pt-1">
                  <span>Dettaglio {g.dateLabel}</span>
                  <button
                    type="button"
                    onClick={() => setExpandedKey(null)}
                    data-testid={`collapse-group-${g.key}`}
                    className="text-text-secondary underline"
                  >
                    comprimi
                  </button>
                </div>
              )}
              {g.entries.map((e) => (
                <CostRow
                  key={e.id}
                  icon={<Clock size={16} />}
                  iconClass={
                    e.removed
                      ? 'bg-[#F2EBDF] border-[#D4C7B5] text-text-muted'
                      : 'bg-[#FFF8EC] border-[#F5C77D] text-accent-primary'
                  }
                  label={`Ora extra ${e.hourIndex}a`}
                  desc={e.removed ? `Rimossa - ${e.removedReason ?? '-'}` : 'Auto · scheduler 60s'}
                  amount={e.amount}
                  removed={e.removed}
                  badge={
                    !e.removed && (
                      <span className="text-[10px] uppercase tracking-[0.5px] font-bold px-2 py-0.5 rounded bg-accent-soft text-accent-primary-text">
                        Auto
                      </span>
                    )
                  }
                  action={
                    !e.removed ? (
                      <button
                        type="button"
                        onClick={() => canRemove && onRemoveClick(e)}
                        disabled={!canRemove}
                        data-testid={`remove-extra-${e.id}`}
                        title={canRemove ? 'Rimuovi' : 'Richiede admin'}
                        className="text-xs font-semibold px-3 py-1 rounded-lg border border-border-input bg-bg-card text-text-secondary hover:bg-[#FAEAE8] hover:border-[#A8453A] hover:text-[#A8453A] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-bg-card"
                      >
                        Rimuovi
                      </button>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.5px] font-bold px-2 py-0.5 rounded bg-bg-base text-text-muted">
                        Rimosso
                      </span>
                    )
                  }
                />
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CostRow({
  icon,
  iconClass,
  label,
  desc,
  amount,
  removed,
  badge,
  action,
}: {
  icon: React.ReactNode;
  iconClass?: string;
  label: string;
  desc?: string;
  amount: string;
  removed?: boolean;
  badge?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-bg-card border border-border-base"
      data-removed={removed ? 'true' : 'false'}
    >
      <span
        className={cn(
          'w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0',
          iconClass,
        )}
      >
        {icon}
      </span>
      <div className="flex-1 flex flex-col">
        <span
          className={cn(
            'text-sm font-semibold text-text-primary',
            removed && 'line-through text-text-muted',
          )}
        >
          {label}
        </span>
        {desc && <span className="text-xs text-text-secondary">{desc}</span>}
      </div>
      {badge}
      <span
        className={cn(
          'text-sm font-bold tabular-nums',
          removed ? 'line-through text-text-muted' : 'text-text-primary',
        )}
      >
        EUR {amount}
      </span>
      {action}
    </div>
  );
}

// --------------------------- TotalsCard ---------------------------

function TotalsCard({ totals }: { totals: CheckoutSummary['totals'] }) {
  return (
    <section
      className="rounded-2xl p-5 text-text-on-accent flex flex-col gap-2"
      style={{
        background: 'linear-gradient(135deg, #E97B3D 0%, #D86A2C 100%)',
        boxShadow: '0 6px 20px rgba(233, 123, 61, 0.25)',
      }}
      data-testid="totals-card"
    >
      <Row label="Gia incassato" value={`EUR ${totals.totalPaid}`} />
      <Row label="Ore extra non rimosse" value={`+ EUR ${totals.extraSum}`} />
      {/* ONDATA 1 fix 1a: consumi room-service addebitati su camera —
          entrano nel saldo incassato al checkout. */}
      {Number(totals.rsVoucherSum ?? '0') > 0 && (
        <Row label="Room service su camera" value={`+ EUR ${totals.rsVoucherSum}`} />
      )}
      <div className="border-t border-white/20 mt-1 pt-3 flex items-center justify-between">
        <span className="text-sm font-semibold opacity-90">Da incassare ora</span>
        <span className="text-2xl font-bold" data-testid="balance-due">
          EUR {totals.balanceDue}
        </span>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="opacity-90">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

// --------------------------- PaymentMethodCard ---------------------------

// QA sweep 2026-07-22 (bug ALTO): da card statica a SELETTORE reale con
// due opzioni Contanti/Carta (tap target >= 44px). L'input "Contanti
// ricevuti" + resto compare in base alla SELEZIONE, non piu' al metodo
// d'ingresso.
function PaymentMethodCard({
  method,
  onMethodChange,
  amountReceived,
  onAmountReceivedChange,
  balanceDue,
}: {
  method: 'cash' | 'card';
  onMethodChange: (m: 'cash' | 'card') => void;
  amountReceived: string;
  onAmountReceivedChange: (v: string) => void;
  balanceDue: string;
}) {
  // ONDATA 1 fix 1e: preview resto = ricevuto − saldo (solo contanti).
  const received = Number.parseFloat(amountReceived);
  const balance = Number.parseFloat(balanceDue);
  const change =
    method === 'cash' && amountReceived !== '' && Number.isFinite(received)
      ? received - balance
      : null;
  const options = [
    { id: 'cash', label: 'Contanti', sub: 'Glory · scontrino unificato', Icon: Banknote },
    { id: 'card', label: 'Carta', sub: 'Revolut · scontrino unificato', Icon: CreditCard },
  ] as const;
  return (
    <section
      className="flex flex-col gap-3 p-4 rounded-xl border border-border-base bg-bg-card"
      data-testid="payment-method-card"
    >
      <h3 className="text-xs uppercase tracking-[0.5px] font-semibold text-text-secondary">
        Metodo di incasso
      </h3>
      <div className="grid grid-cols-2 gap-2.5">
        {options.map(({ id, label, sub, Icon }) => {
          const selected = method === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onMethodChange(id)}
              aria-pressed={selected}
              data-testid={`payment-select-${id}`}
              className={cn(
                'flex items-center gap-3 px-3.5 py-3 min-h-[56px] rounded-xl border text-left transition-colors',
                selected
                  ? 'border-accent-primary bg-accent-soft'
                  : 'border-border-input bg-bg-card hover:bg-bg-base',
              )}
            >
              <span
                className="w-11 h-11 min-h-[44px] min-w-[44px] rounded-[10px] flex items-center justify-center flex-shrink-0"
                style={{ background: '#FCF1EA' }}
              >
                <Icon size={22} className="text-accent-primary" />
              </span>
              <span className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-text-primary">{label}</span>
                <span className="text-xs text-text-secondary truncate">{sub}</span>
              </span>
            </button>
          );
        })}
      </div>
      {method === 'cash' && balance > 0 && (
        <div className="flex items-center gap-3">
          <label className="flex-1 flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary">
              Contanti ricevuti (opzionale)
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={amountReceived}
              onChange={(e) => onAmountReceivedChange(e.target.value)}
              data-testid="amount-received"
              placeholder={`min EUR ${balanceDue}`}
              className="px-3.5 py-2.5 rounded-lg border border-border-input bg-bg-card text-sm text-text-primary"
            />
          </label>
          {change !== null && (
            <span
              data-testid="change-preview"
              className={cn(
                'text-sm font-bold tabular-nums self-end pb-2.5',
                change < 0 ? 'text-red-600' : 'text-text-primary',
              )}
            >
              {change < 0
                ? `Mancano EUR ${Math.abs(change).toFixed(2)}`
                : `Resto: EUR ${change.toFixed(2)}`}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

// --------------------------- ModalFooter ---------------------------

function ModalFooter({
  onCancel,
  onConfirm,
  loading,
  balanceDue,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  balanceDue: string;
}) {
  return (
    <footer className="shrink-0 bg-bg-card border-t border-border-base flex items-center justify-between px-7 py-4">
      <span className="text-xs text-text-secondary" data-testid="checkout-footer-info">
        Saldo da incassare: EUR {balanceDue}
      </span>
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          data-testid="checkout-cancel"
          className="min-h-[44px] bg-bg-card text-text-secondary border border-border-base px-5 py-2.5 rounded-lg text-[13px] font-semibold disabled:opacity-50"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          data-testid="checkout-confirm"
          className="min-h-[44px] bg-accent-primary text-text-on-accent px-7 py-2.5 rounded-lg text-[13px] font-semibold flex items-center gap-1.5 hover:bg-accent-primary-hover disabled:opacity-50"
          style={{ boxShadow: '0 2px 8px rgba(233, 123, 61, 0.25)' }}
        >
          {loading ? 'Check-out…' : 'Conferma check-out'}
        </button>
      </div>
    </footer>
  );
}

// --------------------------- RemoveExtraHourConfirm ---------------------------

const REMOVE_REASONS = [
  'Errore scheduler',
  'Cliente uscito ma non scalato',
  'Pagato in nero',
  'Cortesia direzione',
  'Altro',
];

function RemoveExtraHourConfirm({
  entry,
  onCancel,
  onConfirmed,
}: {
  entry: ExtraHourLogEntry;
  onCancel: () => void;
  onConfirmed: () => void;
}) {
  const [reason, setReason] = useState(REMOVE_REASONS[0] ?? '');
  const [notes, setNotes] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => removeExtraHour(entry.id, { reason, notes: notes || null }),
    onSuccess: () => onConfirmed(),
    onError: (err: unknown) => setErrMsg(formatError(err)),
  });

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: overlay manuale dentro dialog parent
      role="dialog"
      aria-modal="true"
      data-testid="remove-extra-confirm"
      className="fixed inset-0 z-[1100] flex items-center justify-center p-5"
      style={{ background: 'rgba(45, 36, 29, 0.7)' }}
    >
      {/* HM-600 iPad UX: sub-modale safe-area — max 90vh, colonna flex,
          header/footer fissi (shrink-0), corpo scrollabile. */}
      <div
        className="bg-bg-card rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ width: 'min(440px, 96vw)', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}
      >
        <header
          className="shrink-0 flex items-center gap-3 px-6 py-4 border-b border-border-base"
          style={{ background: '#FAEAE8' }}
        >
          <span className="w-9 h-9 rounded-lg bg-[#A8453A] text-white flex items-center justify-center">
            <AlertTriangle size={18} />
          </span>
          <h3 className="text-base font-bold text-text-primary">Rimuovi ora extra</h3>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-3">
          <div
            className="px-4 py-3 rounded-lg flex justify-between items-center"
            style={{ background: '#FCF1EA' }}
          >
            <span className="text-sm font-semibold text-text-primary">
              Ora extra {entry.hourIndex}a
            </span>
            <span className="text-sm font-bold text-text-primary">EUR {entry.amount}</span>
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary">
              Motivo
            </span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="remove-reason"
              className="px-3.5 py-2.5 rounded-lg border border-border-input bg-bg-card text-sm text-text-primary"
            >
              {REMOVE_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary">
              Note (opzionali)
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="remove-notes"
              rows={3}
              className="px-3.5 py-2.5 rounded-lg border border-border-input bg-bg-card text-sm text-text-primary resize-none"
            />
          </label>
          {errMsg && (
            <p role="alert" className="text-xs text-red-600">
              {errMsg}
            </p>
          )}
        </div>
        <footer
          className="shrink-0 px-6 py-4 border-t border-border-base flex justify-end gap-2.5"
          style={{ background: '#FAF7F2' }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={mutation.isPending}
            data-testid="remove-cancel"
            className="bg-bg-card text-text-secondary border border-border-base px-5 py-2 rounded-lg text-[13px] font-semibold"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !reason}
            data-testid="remove-confirm"
            className="text-white border-0 px-5 py-2 rounded-lg text-[13px] font-semibold"
            style={{ background: '#A8453A' }}
          >
            {mutation.isPending ? 'Rimozione…' : 'Conferma rimozione'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// --------------------------- helpers ---------------------------

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'forbidden') return 'Richiede ruolo admin per rimuovere questa ora extra.';
    if (err.code === 'invalid_state') return 'Stato booking non consente check-out.';
    if (err.status === 404) return 'Risorsa non trovata.';
    return `Errore ${err.status}${err.code ? ` (${err.code})` : ''}`;
  }
  if (err instanceof Error) return err.message;
  return 'Errore sconosciuto';
}
